import { useCallback, useMemo, useState } from 'react';

import { NBA_PANEL_MODE_BY_ACTION } from '../constants/nextBestAction.js';
import { useNextBestActions } from './useNextBestActions.js';
import { useStartNextBestActionWorkflow } from './useStartNextBestActionWorkflow.js';

/**
 * Everything the profile page needs for the Next Best Action feature (CT-5713),
 * so the page only wires it: the suggestions (fetched ONCE per profile, so a tab
 * switch never re-requests them), the slide-out and Start workflow. The summary
 * row's state and its Collapse belong to the pair with How to approach (CT-5714),
 * see `useProfileSummary`.
 *
 * The slide-out belongs to the record it was opened on. The page stays mounted
 * across records, so the panel remembers its `recordKey` and resolves to nothing
 * on another record or while a list is loading — it can never show, or start a
 * workflow from, the previous record's suggestion.
 *
 * Actions that open the slide-out are handled here (`handleAction`); an action
 * that goes to the Concierge hands off inside its own button
 * (`NextBestActionActionButton`, CT-5721), so the shell chat never re-renders the
 * page.
 *
 * @param {object} options
 * @param {string} options.entityType
 * @param {string} options.entityId
 * @param {string} options.entityName — the name the header shows.
 * @param {() => void} [options.onStarted] — after a workflow starts (the page
 *   re-reads the header for its chip).
 */
export function useProfileNextBestActions({ entityType, entityId, entityName, onStarted } = {}) {
  const nba = useNextBestActions({ entityType, entityId });
  const { items, updatedAt, checkedAt, restricted, loading, error, refetch, refresh: refreshSuggestions } = nba;
  const recordKey = `${entityType}/${entityId}`;

  const [panel, setPanel] = useState(null);
  const closePanel = useCallback(() => setPanel(null), []);
  const openDetails = useCallback((item) => setPanel({ recordKey, itemId: item.id, mode: 'details' }), [recordKey]);
  // A tab opens the same slide-out for a suggestion it lists (Tasks' Review workflow, CT-5722).
  const openPanel = useCallback((itemId, mode) => setPanel({ recordKey, itemId, mode }), [recordKey]);
  const handleAction = useCallback(
    (item, action) => {
      const mode = NBA_PANEL_MODE_BY_ACTION[action?.type];
      if (mode) setPanel({ recordKey, itemId: item.id, mode });
    },
    [recordKey],
  );
  const panelItem = useMemo(
    () =>
      panel?.recordKey === recordKey && !loading ? (items.find((item) => item.id === panel.itemId) ?? null) : null,
    [panel, recordKey, loading, items],
  );

  const conciergeEntity = useMemo(
    () => ({ type: entityType, id: entityId, title: entityName }),
    [entityType, entityId, entityName],
  );

  // How many workflows have started from here: a started workflow takes its
  // suggestion out of the list, and the tabs that list suggestions (Tasks,
  // CT-5722) re-read when this changes.
  const [workflowsStarted, setWorkflowsStarted] = useState(0);
  const handleStarted = useCallback(() => {
    setPanel(null);
    setWorkflowsStarted((count) => count + 1);
    refreshSuggestions();
    onStarted?.();
  }, [refreshSuggestions, onStarted]);
  const { start: startWorkflow, starting: startingWorkflow } = useStartNextBestActionWorkflow({
    entityType,
    entityId,
    entityName,
    onStarted: handleStarted,
  });

  return useMemo(
    () => ({
      loading,
      error,
      items,
      updatedAt,
      checkedAt,
      restricted,
      refetch,
      panelItem,
      panelMode: panelItem ? panel.mode : null,
      openDetails,
      openPanel,
      closePanel,
      handleAction,
      conciergeEntity,
      startWorkflow,
      startingWorkflow,
      workflowsStarted,
    }),
    [
      loading,
      error,
      items,
      updatedAt,
      checkedAt,
      restricted,
      refetch,
      panelItem,
      panel,
      openDetails,
      openPanel,
      closePanel,
      handleAction,
      conciergeEntity,
      startWorkflow,
      startingWorkflow,
      workflowsStarted,
    ],
  );
}

export default useProfileNextBestActions;
